import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Search, Link2, Unlink, Ban, CopyCheck, Phone, FolderOpen, RotateCw, CheckCircle2, Landmark, XCircle, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { ausWizardBorrowerSearch, matchAusPayment } from "@/lib/api";

export const fmtAUD = (n) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", minimumFractionDigits: 2,
}).format(Number(n || 0));

// Calendar dates (YYYY-MM-DD) are Sydney business dates — render them as-is, never via local tz.
export const fmtDate = (s) => {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s;
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
};
export const sydneyDateOf = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Sydney", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
    const g = (t) => parts.find((x) => x.type === t)?.value;
    return `${g("year")}-${g("month")}-${g("day")}`;
  } catch { return ""; }
};
export const fmtSydney = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Sydney", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const STATUS = {
  matched:        { label: "Ready",               cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  manual_matched: { label: "Ready (manual)",      cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  unmatched:      { label: "Needs match",         cls: "bg-amber-100 text-amber-700 border-amber-300" },
  duplicate:      { label: "Already in LoanDisk", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  posted:         { label: "Posted",              cls: "bg-slate-100 text-slate-700 border-slate-300" },
  settling:       { label: "BECS settling",       cls: "bg-sky-100 text-sky-700 border-sky-300" },
};
const LD_STATUS = { 1: "Active", 2: "Closed", 3: "Legal", 4: "Restructured" };

export const SOURCE_LABEL = { gocardless_au: "GoCardless", capay_bank: "Capay bank" };

export function SchemeChip({ label }) {
  const cls = label === "PayTo" ? "bg-violet-50 text-violet-700 border-violet-300"
    : label === "Bank credit" ? "bg-teal-50 text-teal-700 border-teal-300"
    : "bg-sky-50 text-sky-700 border-sky-300";
  return <Badge variant="outline" className={`text-[10px] px-1.5 ${cls}`}>{label || "—"}</Badge>;
}

export function SourceChip({ source }) {
  const capay = source === "capay_bank";
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 ${capay ? "bg-teal-50 text-teal-800 border-teal-300" : "bg-slate-50 text-slate-600 border-slate-300"}`} data-testid={`lra-source-chip-${source}`}>
      {SOURCE_LABEL[source] || source}
    </Badge>
  );
}

export function LateFailureBanner({ data, onReverse, onAck, busy }) {
  if (!data?.count) return null;
  return (
    <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2" data-testid="lra-late-failures">
      <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5"><XCircle className="w-4 h-4" />{data.count} posted repayment{data.count === 1 ? "" : "s"} later FAILED in GoCardless · {fmtAUD(data.total)} — reverse in LoanDisk</p>
      <p className="text-[11px] text-red-700/80">BECS "confirmed" is not final: the customer's bank returned these after we posted them. Reverse deletes exactly the repayment the portal keyed; use "Already fixed" if it was removed by hand.</p>
      <div className="divide-y divide-red-200 border-t border-red-200">
        {data.rows.map((r) => (
          <div key={r.payment_id} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-xs" data-testid={`lra-late-failure-${r.payment_id}`}>
            <span className="min-w-0 truncate"><b>{r.sfa_number || r.loan_application_id}</b> {r.borrower_name ? `· ${r.borrower_name}` : ""} · charged {fmtDate(r.date)} · {fmtAUD(r.amount)} · LoanDisk #{r.repayment_id} · failed {fmtDate(r.late_failure_at)}{r.late_failure_cause ? ` (${r.late_failure_cause.replace(/_/g, " ")})` : ""}</span>
            <span className="flex gap-1.5">
              <Button size="sm" className="h-6 px-2 text-[11px] bg-red-600 hover:bg-red-700 text-white" disabled={busy === r.payment_id} onClick={() => onReverse(r)} data-testid={`lra-late-reverse-${r.payment_id}`}>
                {busy === r.payment_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Reverse in LoanDisk"}
              </Button>
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] border-red-300 text-red-700" disabled={busy === r.payment_id} onClick={() => onAck(r)} data-testid={`lra-late-ack-${r.payment_id}`}>Already fixed</Button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function P2PKeptChip({ row, onMarkKept, busy }) {
  const p = row.p2p_prompt;
  if (!p) return null;
  const partial = !p.covers;
  return (
    <button type="button" onClick={() => onMarkKept?.(row)} disabled={busy} data-testid={`lra-p2p-kept-${row.payment_id}`}
      title={`Pending promise to pay: ${fmtAUD(p.promised_amount)} on ${fmtDate(p.promised_at)}${p.handler ? ` · ${p.handler}` : ""} — this payment landed ${p.days_gap === 0 ? "the same day" : `${p.days_gap} day${p.days_gap === 1 ? "" : "s"} ${row.date < p.promised_at ? "early" : "late"}`}. Click to mark the promise kept${partial ? " (partial — paid less than promised)" : ""}.`}
      className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${partial ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"} disabled:opacity-60`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <HandCoins className="w-3 h-3" />}
      Promise kept{partial ? " (partial)" : ""} — mark it · {fmtAUD(p.promised_amount)} {fmtDate(p.promised_at)}
    </button>
  );
}

export function AusPaymentRow({ row, onMatch, onUnmatch, onDismiss, onPostAnyway, onPickSuggestion, onMarkP2PKept, markingP2P, postingAnyway }) {
  const meta = STATUS[row.status] || STATUS.unmatched;
  const isPosted = row.status === "posted";
  const isDup = row.status === "duplicate";
  const isCapay = row.source === "capay_bank";
  return (
    <div className={`grid grid-cols-12 gap-3 items-center px-4 py-2.5 border-b text-sm ${isPosted ? "bg-slate-50/60" : ""}`}
      data-testid={`lra-row-${row.payment_id}`}>
      <div className="col-span-5 min-w-0">
        <p className="font-medium truncate" title={row.description}>{isCapay ? (row.payer_name || row.description) : row.description}</p>
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <SourceChip source={row.source} />
          <SchemeChip label={row.scheme_label} />
          <span>{isCapay ? "received" : "charged"} {fmtDate(row.date)}</span>
          {isCapay ? (
            <span className="truncate" title={`Payer reference: ${row.payment_reference || "—"} · Capay ${row.reference}`}>ref <b className="font-medium text-slate-700">{row.payment_reference || "—"}</b> · <span className="font-mono">{row.reference}</span></span>
          ) : <span className="font-mono">{row.payment_id}</span>}
        </div>
        {isCapay ? (
          <div className="text-[11px] flex items-center gap-2 mt-0.5 text-sky-700" data-testid={`lra-row-times-${row.payment_id}`}>
            <Landmark className="w-3 h-3 inline -mt-0.5" />In our Capay account {fmtDate(row.payout_arrival_date || row.date)} · Capay {row.capay_status?.toLowerCase()}
          </div>
        ) : (
        <div className="text-[11px] flex items-center gap-2 mt-0.5" data-testid={`lra-row-times-${row.payment_id}`}>
          <span className="text-emerald-700" title="GoCardless confirmed the money was collected (Sydney time)">
            <CheckCircle2 className="w-3 h-3 inline mr-0.5 -mt-0.5" />Cleared {row.confirmed_at ? fmtSydney(row.confirmed_at) : (row.gc_status === "paid_out" ? "—" : "pending")}
          </span>
          <span className={row.payout_arrival_date || row.paid_out_at ? "text-sky-700" : "text-muted-foreground"}
            title={row.payout_reference ? `GoCardless payout ${row.payout_reference} — arrives in the SimplyFunded bank account on this date` : "Swept to the SimplyFunded bank account"}>
            <Landmark className="w-3 h-3 inline mr-0.5 -mt-0.5" />
            {row.payout_arrival_date ? `Paid out ${fmtDate(row.payout_arrival_date)}` : row.paid_out_at ? `Paid out ${fmtSydney(row.paid_out_at)}` : "Payout pending"}
            {row.payout_reference && <span className="font-mono text-[10px] text-muted-foreground ml-1">{row.payout_reference}</span>}
          </span>
        </div>
        )}
      </div>
      <div className="col-span-2 text-right font-semibold tabular-nums" data-testid={`lra-amount-${row.payment_id}`}>{fmtAUD(row.amount)}</div>
      <div className="col-span-3 min-w-0">
        {row.matched_loan ? (
          <>
            <p className="truncate font-medium" title={row.matched_loan.borrower_name}>{row.matched_loan.borrower_name || "—"}</p>
            <p className="text-[11px] text-muted-foreground truncate">
              {row.matched_loan.loan_application_id} · loan {row.matched_loan.loan_id}
              {row.match_type === "manual" && <span className="text-sky-700"> · manual</span>}
              {row.match_type === "company_name" && <span className="text-teal-700" title={`Auto-matched on payer name (${row.match_score}% similar) — same rule as the UK bank feed`}> · name match {row.match_score}%</span>}
              {row.match_type === "capay_va" && <span className="text-emerald-700" title="Paid into the customer's own Capay Virtual Account — one-to-one match" data-testid="capay-va-match-badge"> · VA match{row.first_party === false ? " · payer name differs" : ""}</span>}
            </p>
            {isDup && row.duplicate_info && (
              <p className="text-[11px] text-blue-700 truncate" title={row.duplicate_info.existing_notes}>
                {row.duplicate_info.source === "precheck" ? "keyed in LoanDisk already" : "existing"} #{row.duplicate_info.existing_repayment_id}{row.duplicate_info.existing_collector && ` · by ${row.duplicate_info.existing_collector}`}
              </p>
            )}
            {isPosted && row.posted_info && <p className="text-[11px] text-slate-500 truncate">LoanDisk #{row.posted_info.repayment_id} · {row.posted_info.posted_by}</p>}
          </>
        ) : <span className="text-xs text-muted-foreground italic">No match</span>}
        {row.status === "unmatched" && row.unmatched_reason && (
          <p className="text-[11px] text-amber-700 truncate" title={row.unmatched_reason} data-testid={`lra-unmatched-reason-${row.payment_id}`}>{row.unmatched_reason}</p>
        )}
        {row.p2p_prompt && <div className="mt-1"><P2PKeptChip row={row} onMarkKept={onMarkP2PKept} busy={markingP2P === row.payment_id} /></div>}
        {row.status === "unmatched" && (row.suggestions || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1" data-testid={`lra-suggestions-${row.payment_id}`}>
            {row.suggestions.slice(0, 2).map((sg) => (
              <button key={sg.loan_id} type="button" onClick={() => onPickSuggestion?.(row, sg)} data-testid={`lra-suggest-${row.payment_id}-${sg.loan_id}`}
                className="text-[11px] px-1.5 py-0.5 rounded border border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 truncate max-w-[220px]" title={`Confirm match to ${sg.borrower_name} (${sg.loan_application_id}) — ${sg.score}% name similarity`}>
                ✓ {sg.borrower_name} · {sg.score}%
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1.5">
        <Badge variant="outline" className={`${meta.cls} text-[10px] px-1.5`}>{meta.label}</Badge>
        {row.status === "settling" ? (
          <span className="text-[11px] text-sky-700 text-right leading-tight" title="GoCardless has 'confirmed' this BECS debit but the bank can still return it as a late failure for several days. It becomes Ready once GoCardless pays it out." data-testid={`lra-settling-${row.payment_id}`}>
            posts once paid out{row.payout_arrival_date ? ` · ${fmtDate(row.payout_arrival_date)}` : ""}
          </span>
        ) : !isPosted && (
          <>
            {isDup ? (
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50" onClick={onPostAnyway} disabled={postingAnyway} data-testid={`lra-post-anyway-${row.payment_id}`}>
                {postingAnyway ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CopyCheck className="w-3.5 h-3.5 mr-1" />}Post anyway
              </Button>
            ) : row.match_type === "manual" ? (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onUnmatch} title="Remove manual match" data-testid={`lra-unmatch-${row.payment_id}`}><Unlink className="w-3.5 h-3.5" /></Button>
            ) : (
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={onMatch} data-testid={`lra-match-${row.payment_id}`}><Link2 className="w-3.5 h-3.5 mr-1" />{row.matched_loan ? "Change" : "Match"}</Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500" onClick={onDismiss} title="Hide from this list" data-testid={`lra-dismiss-${row.payment_id}`}><Ban className="w-3.5 h-3.5" /></Button>
          </>
        )}
      </div>
    </div>
  );
}

const CAUSE = {
  insufficient_funds: "Insufficient funds", refer_to_payer: "Refer to payer", unsuccessful_direct_debit: "Bank rejected",
  mandate_cancelled: "Mandate cancelled", bank_account_closed: "Account closed", authorisation_disputed: "Authorisation disputed",
  payment_stopped: "Payment stopped by customer", direct_debit_not_enabled: "DD not enabled", invalid_bank_details: "Invalid bank details",
};

export function AusFailedRow({ item }) {
  const f = item.failure || {};
  const daysTo2 = item.days_since_charge == null ? null : 2 - item.days_since_charge;
  return (
    <div className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 border-b text-sm" data-testid={`lra-failed-${item.payment_id}`}>
      <div className="col-span-4 min-w-0">
        <p className="font-medium truncate" title={item.description}>{item.borrower_name || item.description}</p>
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5"><SourceChip source={item.source || "gocardless_au"} /><SchemeChip label={item.scheme_label} /><span>{item.sfa_number}</span><span>charged {fmtDate(item.charge_date)}</span></div>
      </div>
      <div className="col-span-2 text-right font-semibold tabular-nums text-red-700">{fmtAUD(item.amount)}</div>
      <div className="col-span-3 min-w-0">
        <p className="text-xs font-medium text-red-800 truncate" title={f.description}>{CAUSE[f.cause] || f.cause || item.gc_status}{f.reason_code && <span className="text-red-600/70"> · {f.reason_code}</span>}</p>
        <p className="text-[11px] text-muted-foreground truncate" data-testid={`lra-failed-time-${item.payment_id}`}>
          <XCircle className="w-3 h-3 inline mr-0.5 -mt-0.5 text-red-500" />{f.at ? `Failed ${fmtSydney(f.at)}` : "Failed"} · {f.will_attempt_retry ? "GoCardless will retry" : "no automatic retry"}
        </p>
      </div>
      <div className="col-span-3 flex items-center justify-end gap-1.5">
        {daysTo2 != null && (
          <Badge variant="outline" className={`text-[10px] ${daysTo2 <= 0 ? "bg-red-100 text-red-700 border-red-300" : "bg-amber-50 text-amber-700 border-amber-300"}`} data-testid={`lra-failed-age-${item.payment_id}`}>
            {daysTo2 <= 0 ? `Arrears · day ${item.days_since_charge}` : `Arrears in ${daysTo2}d`}
          </Badge>
        )}
        {item.loan_id && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" title="Open in LoanDisk" asChild>
            <a href={`https://app.loandisk.com/loan/view/${item.loan_id}`} target="_blank" rel="noreferrer" data-testid={`lra-failed-open-${item.payment_id}`}><FolderOpen className="w-3.5 h-3.5" /></a>
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" title="Copy a call/SMS note for this failure"
          onClick={() => { navigator.clipboard?.writeText(`${item.sfa_number} · ${item.borrower_name || ""} · ${fmtAUD(item.amount)} ${item.scheme_label} failed ${fmtDate(item.charge_date)} — ${CAUSE[f.cause] || f.cause || ""}`); toast.success("Copied"); }}
          data-testid={`lra-failed-copy-${item.payment_id}`}><Phone className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}

export function AusInFlightRow({ item }) {
  const submitted = item.gc_status === "submitted";
  return (
    <div className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 border-b text-sm" data-testid={`lra-inflight-${item.payment_id}`}>
      <div className="col-span-4 min-w-0">
        <p className="font-medium truncate" title={item.description}>{item.borrower_name || item.description}</p>
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5"><SourceChip source={item.source || "gocardless_au"} /><SchemeChip label={item.scheme_label} /><span>{item.sfa_number}</span></div>
      </div>
      <div className="col-span-2 text-right font-semibold tabular-nums text-amber-800">{fmtAUD(item.amount)}</div>
      <div className="col-span-2 text-xs" data-testid={`lra-inflight-dates-${item.payment_id}`}>
        <p><span className="text-muted-foreground">Charge</span> {fmtDate(item.charge_date)}</p>
        <p><span className="text-muted-foreground">Submitted</span> {submitted ? <span title={fmtSydney(item.submitted_at) + " Sydney"}>{fmtDate(item.submitted_date) }</span> : <span className="text-muted-foreground">not yet</span>}</p>
      </div>
      <div className="col-span-2 text-xs">
        <p><span className="text-muted-foreground">Result due</span> {item.expected_result_date ? fmtDate(item.expected_result_date) : "—"}</p>
        {item.days_in_flight != null && <p className="text-muted-foreground">{item.days_in_flight === 0 ? "submitted today" : `${item.days_in_flight}d in flight`}</p>}
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1.5 text-xs">
        {item.overdue ? (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-[10px]" data-testid={`lra-inflight-overdue-${item.payment_id}`}>Result overdue</Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px] gap-1"><RotateCw className="w-3 h-3" />
            {item.source === "capay_bank" ? `Capay ${(item.capay_status || "pending").toLowerCase()}` : submitted ? "At the bank" : item.gc_status === "pending_submission" ? "Scheduled" : "Awaiting approval"}
          </Badge>
        )}
      </div>
    </div>
  );
}

export function AusMatchDialog({ open, row, onClose, onMatched, actioningUser }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [picking, setPicking] = useState(null);

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); return; }
    if (row?.sfa_number) setQ(row.sfa_number.split("/")[0]);
  }, [open, row]);

  const search = useCallback(async (qq) => {
    if (!qq || qq.trim().length < 2) { setResults([]); return; }
    setBusy(true);
    try { setResults((await ausWizardBorrowerSearch(qq.trim())).results || []); }
    catch { toast.error("Search failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { const t = setTimeout(() => search(q), 350); return () => clearTimeout(t); }, [q, search]);

  const pick = async (r) => {
    setPicking(r.loan_id);
    try {
      await matchAusPayment(row.payment_id, { loan_id: r.loan_id, loan_application_id: r.loan_application_id, borrower_name: r.borrower_name, matched_by: actioningUser });
      toast.success(`Matched to ${r.loan_application_id}`);
      onMatched();
    } catch (e) { toast.error(e?.response?.data?.detail || "Match failed"); }
    finally { setPicking(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="lra-match-dialog">
        <DialogHeader>
          <DialogTitle>Match to a LoanDisk AU loan</DialogTitle>
          <DialogDescription className="truncate">{row?.description} · <b>{fmtAUD(row?.amount)}</b></DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="SFA-1234 or business name" className="pl-8" data-testid="lra-match-search" />
        </div>
        {(row?.suggestions || []).length > 0 && (
          <div className="border rounded divide-y bg-teal-50/40" data-testid="lra-match-suggestions">
            <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-teal-800">Suggested from payer name — confirm one</p>
            {row.suggestions.map((sg) => (
              <button key={`sg-${sg.loan_id}`} onClick={() => pick(sg)} disabled={!!picking} data-testid={`lra-match-suggest-${sg.loan_id}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-teal-100/60 flex items-center justify-between gap-2">
                <span className="min-w-0"><span className="font-medium">{sg.loan_application_id}</span> <span className="text-muted-foreground truncate">· {sg.borrower_name}</span></span>
                <Badge variant="outline" className="text-[10px] bg-white border-teal-300 text-teal-800">{picking === sg.loan_id ? <Loader2 className="w-3 h-3 animate-spin" /> : `${sg.score}% · ${LD_STATUS[sg.loan_status_id] || sg.loan_status_id}`}</Badge>
              </button>
            ))}
          </div>
        )}
        <div className="max-h-72 overflow-y-auto divide-y border rounded">
          {busy && <div className="p-4 text-center text-xs text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Searching LoanDisk AU…</div>}
          {!busy && results.length === 0 && q.length >= 2 && <div className="p-4 text-center text-xs text-muted-foreground">No loans found</div>}
          {results.map((r) => (
            <button key={r.loan_id} onClick={() => pick(r)} disabled={!!picking} data-testid={`lra-match-pick-${r.loan_id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-2">
              <span className="min-w-0">
                <span className="font-medium">{r.loan_application_id}</span> <span className="text-muted-foreground truncate">· {r.borrower_name || "—"}</span>
                <span className="block text-[11px] text-muted-foreground">loan {r.loan_id} · principal {fmtAUD(r.principal)}</span>
              </span>
              <Badge variant="outline" className={`text-[10px] ${r.loan_status_id === 1 ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-600"}`}>
                {picking === r.loan_id ? <Loader2 className="w-3 h-3 animate-spin" /> : (LD_STATUS[r.loan_status_id] || r.loan_status_id)}
              </Badge>
            </button>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const BUCKET = {
  cleared:   "bg-emerald-50 text-emerald-700 border-emerald-300",
  failed:    "bg-red-50 text-red-700 border-red-300",
  in_flight: "bg-amber-50 text-amber-700 border-amber-300",
};
const KIND = {
  matched:  { label: "Matched",            cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  mismatch: { label: "Amount differs",     cls: "bg-orange-100 text-orange-700 border-orange-300" },
  missing:  { label: "Due · no GC collection", cls: "bg-red-100 text-red-700 border-red-300" },
  surplus:  { label: "GC only · not due",  cls: "bg-sky-100 text-sky-700 border-sky-300" },
};

export function DueReconRow({ row }) {
  const k = KIND[row.kind] || KIND.matched;
  return (
    <div className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 border-b text-sm" data-testid={`lra-due-row-${row.kind}-${(row.sfa_number || "").replace(/[^A-Z0-9]/gi, "")}`}>
      <div className="col-span-4 min-w-0">
        <p className="font-medium truncate" title={row.borrower}>{row.borrower || "—"}</p>
        <p className="text-[11px] text-muted-foreground">{row.sfa_number}{row.loan_status ? ` · ${row.loan_status}` : ""}{row.past_due > 0 ? ` · past due ${fmtAUD(row.past_due)}` : ""}</p>
      </div>
      <div className="col-span-2 text-right tabular-nums">
        <p className="text-[10px] uppercase text-muted-foreground">LoanDisk due</p>
        <p className="font-semibold">{row.due_amount != null ? fmtAUD(row.due_amount) : <span className="text-muted-foreground">—</span>}</p>
      </div>
      <div className="col-span-2 text-right tabular-nums">
        <p className="text-[10px] uppercase text-muted-foreground">GoCardless</p>
        <p className={`font-semibold ${row.gc_count ? "" : "text-red-700"}`}>{row.gc_count ? fmtAUD(row.gc_total) : "none"}</p>
      </div>
      <div className="col-span-2 flex flex-wrap gap-1 justify-end">
        {(row.gc_payments || []).map((p) => (
          <Badge key={p.payment_id} variant="outline" className={`text-[10px] ${BUCKET[p.bucket] || ""}`} title={`${p.payment_id} · ${p.gc_status}`}>
            {p.scheme_label === "PayTo" ? "PayTo" : "DD"} {fmtAUD(p.amount)} · {p.bucket === "in_flight" ? "at bank" : p.bucket}
          </Badge>
        ))}
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1.5">
        {row.kind === "mismatch" && <span className={`text-xs tabular-nums ${row.diff > 0 ? "text-sky-700" : "text-red-700"}`}>{row.diff > 0 ? "+" : ""}{fmtAUD(row.diff)}</span>}
        <Badge variant="outline" className={`text-[10px] ${k.cls}`}>{k.label}</Badge>
      </div>
    </div>
  );
}


export function PayoutCard({ po, onTick, ticking }) {
  const [open, setOpen] = useState(false);
  const ticked = !!po.tick?.ticked;
  return (
    <div className={`border-b ${ticked ? "bg-emerald-50/40" : ""}`} data-testid={`lra-payout-${po.reference}`}>
      <div className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 text-sm">
        <div className="col-span-1 flex items-center">
          <input type="checkbox" checked={ticked} disabled={ticking} onChange={(e) => onTick(po, e.target.checked)}
            className="w-4 h-4 accent-emerald-600 cursor-pointer" title={ticked ? `Reconciled by ${po.tick.ticked_by} · ${fmtSydney(po.tick.ticked_at)}` : "Tick when this deposit is matched on the bank statement"}
            data-testid={`lra-payout-tick-${po.reference}`} />
        </div>
        <div className="col-span-3 min-w-0">
          <p className="font-mono font-semibold truncate" title="Bank statement reference">{po.reference}</p>
          <p className="text-[11px] text-muted-foreground">Arrives {fmtDate(po.arrival_date)} · GC {po.status}{po.deducted_fees ? ` · fees ${fmtAUD(po.deducted_fees)}` : ""}</p>
        </div>
        <div className="col-span-2 text-right tabular-nums">
          <p className="text-[10px] uppercase text-muted-foreground">Deposit</p>
          <p className="font-bold text-base" data-testid={`lra-payout-amount-${po.reference}`}>{fmtAUD(po.amount, 2)}</p>
        </div>
        <div className="col-span-3 text-xs">
          <p><span className="text-muted-foreground">{po.payments_count} payments</span> · PayTo {fmtAUD(po.by_scheme?.PayTo, 2)} · DD {fmtAUD(po.by_scheme?.["Direct Debit"], 2)}</p>
          <p className="text-muted-foreground">charged {po.charge_dates.map(fmtDate).join(", ") || "—"}</p>
          {po.deductions_count > 0 && (
            <p className="text-red-700" data-testid={`lra-payout-deductions-${po.reference}`} title={(po.deductions || []).map((d) => `${d.type} ${d.payment_id || ""} ${d.amount}`).join("\n")}>
              − {fmtAUD(po.deductions_total, 2)} clawed back · {po.deductions_count} late failure{po.deductions_count === 1 ? "" : "s"}/refund{po.deductions_count === 1 ? "" : "s"}
            </p>
          )}
        </div>
        <div className="col-span-2 text-right">
          {Math.abs(po.unaccounted) > 0.01 ? (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-[10px]" title="Payout amount not covered by payments we have mirrored (older charge dates outside the sync window, or fees/refunds)">
              {fmtAUD(po.unaccounted, 2)} unaccounted
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-[10px]">Payments add up</Badge>
          )}
        </div>
        <div className="col-span-1 flex justify-end">
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setOpen((o) => !o)} data-testid={`lra-payout-toggle-${po.reference}`}>{open ? "Hide" : "Show"}</Button>
        </div>
      </div>
      {ticked && po.tick?.ticked_by && <p className="px-4 pb-2 -mt-1 text-[11px] text-emerald-700">Reconciled by {po.tick.ticked_by} · {fmtSydney(po.tick.ticked_at)}{po.tick.note ? ` · ${po.tick.note}` : ""}</p>}
      {open && (
        <div className="bg-slate-50 border-t" data-testid={`lra-payout-payments-${po.reference}`}>
          {po.payments.map((p) => (
            <div key={p.payment_id} className="grid grid-cols-12 gap-3 px-6 py-1.5 border-b text-xs items-center">
              <div className="col-span-5 truncate"><span className="font-medium">{p.borrower_name || p.description}</span> <span className="text-muted-foreground">· {p.loan_application_id || p.sfa_number}</span></div>
              <div className="col-span-2"><SchemeChip label={p.scheme_label} /></div>
              <div className="col-span-2 text-muted-foreground">charged {fmtDate(p.charge_date)}</div>
              <div className="col-span-3 text-right font-semibold tabular-nums">{fmtAUD(p.amount, 2)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
